/**
 * Types for the vendored AppStream embed SDK (`public/appstream_embed.js`),
 * which attaches itself to `window.AppStream`. The upstream script ships no
 * types, so this is the surface the portal actually uses, no more.
 */

export interface AppStreamEmbedInstance {
  endSession(): void;
  destroy(): void;
  addEventListener(event: string, callback: (payload: Record<string, unknown>) => void): void;
}

interface AppStreamEmbedConstructor {
  new (
    containerId: string,
    options: { sessionURL: string; userInterfaceConfig?: Record<string, unknown> },
  ): AppStreamEmbedInstance;
  Options: { HIDDEN_ELEMENTS: string };
  Elements: {
    TOOLBAR: string;
    CATALOG_BUTTON: string;
    WINDOW_SWITCHER_BUTTON: string;
    SETTINGS_BUTTON: string;
  };
  Events: {
    SESSION_STATE_CHANGE: string;
    SESSION_INTERFACE_STATE_CHANGE: string;
    SESSION_ERROR: string;
  };
  EventParams: { STATUS: string };
  SessionStatus: Record<string, string>;
}

export interface AppStreamGlobal {
  Embed: AppStreamEmbedConstructor;
}

declare global {
  interface Window {
    AppStream?: AppStreamGlobal;
  }
}

/**
 * The SDK is a plain <script> in index.html, so it is present on load in
 * practice — but a blocked or failed fetch would otherwise surface as
 * "Cannot read properties of undefined", which says nothing useful to a customer
 * who cannot launch.
 */
export function requireAppStream(): AppStreamGlobal {
  const sdk = window.AppStream;
  if (!sdk?.Embed) {
    throw new Error('The AppStream viewer failed to load. Reload the page and try again.');
  }
  return sdk;
}
